import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { OwnerLoginRequestSchema, type OwnerLoginRequest } from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { useOwnerLogin, useOwnerSession } from '../hooks/useOwnerSession.js'
import { OwnerThemeToggle } from '../components/OwnerThemeToggle.js'
import { PasswordInput } from '../../shared/PasswordInput.js'
import { NayaWordmark } from '../../shared/brand/NayaWordmark.js'

/**
 * Owner login — email + password (email is the globally-unique identifier;
 * no tenant slug). On success, lands on /dashboard. An already-signed-in
 * owner skips the form.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const { data: me, isLoading: meLoading } = useOwnerSession()
  const login = useOwnerLogin()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OwnerLoginRequest>({
    resolver: zodResolver(OwnerLoginRequestSchema),
  })

  if (!meLoading && me) return <Navigate to="/dashboard" replace />

  function onSubmit(values: OwnerLoginRequest) {
    login.mutate(values, {
      onSuccess: () => navigate('/dashboard', { replace: true }),
      onError: (err) => {
        const message =
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Something went wrong. Please try again.'
        setError('root', { message })
      },
    })
  }

  return (
    <div className="owner-root">
      <div className="owner-auth-screen">
        <div className="owner-auth-card">
          <div className="owner-auth-brand">
            <NayaWordmark className="mark" />
          </div>
          <p className="owner-auth-title">Log in to your dashboard</p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {errors.root && (
              <div className="owner-auth-error" role="alert">
                {errors.root.message}
              </div>
            )}

            <div className="owner-field">
              <label className="owner-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                className="owner-input"
                aria-invalid={errors.email ? 'true' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p className="owner-field-error">{errors.email.message}</p>
              )}
            </div>

            <div className="owner-field">
              <label className="owner-label" htmlFor="password">
                Password
              </label>
              <PasswordInput
                id="password"
                classPrefix="owner"
                autoComplete="current-password"
                aria-invalid={errors.password ? 'true' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p className="owner-field-error">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              className="owner-btn owner-btn-primary owner-btn-block"
              disabled={isSubmitting || login.isPending}
            >
              {login.isPending ? 'Logging in…' : 'Log in'}
            </button>
          </form>
          <p
            className="owner-hint"
            style={{ marginTop: 18, textAlign: 'center' }}
          >
            Working the counter?{' '}
            <Link to="/staff/login">Sign in as staff</Link>
          </p>
        </div>
        <OwnerThemeToggle />
      </div>
    </div>
  )
}
